import moment from "moment";

const monthsLabels = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

/**
 * Calculates amount of measures created per month
 *
 * @param data The raw data to use for conversion
 */
export function calculateMeasuresPerMonthData(data) {
  const dataWithMeasure = data.filter(v => "measureId" in v);

  const measuresPerMonth = dataWithMeasure.reduce((acc, curr) => {
    const currCreationTime = moment(curr["measureCreateTime"]);
    const month = currCreationTime.month();
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, new Array<number>(12).fill(0));

  // chart js conversion

  return {
    datasets: [{ label: "Measures created per month", data: measuresPerMonth }],
    labels: monthsLabels
  };
}

/**
 * Calculates the average processing time of measures per month per finding-type
 *
 * @param data The raw data to use for conversion
 */
export function calculateAverageProcessingTimeData(data) {
  const dataWithMeasure = data.filter(v => "measureId" in v);

  const averageProcessingTimePerFindingType = {};

  // count measures per month per finding type and sum their durations
  dataWithMeasure.forEach(v => {
    const currentProcessingTime = moment
      .duration(
        moment(v["measureLastUpdateTime"]).diff(moment(v["measureCreateTime"]))
      )
      .asDays();

    const currentMonth = moment(v["measureCreateTime"]).month();
    const currentFindingType = v["findingTypeText"];

    const currFindingTypeData =
      averageProcessingTimePerFindingType[currentFindingType];

    // init data for current finding type if not existing yet
    if (currFindingTypeData === undefined) {
      averageProcessingTimePerFindingType[currentFindingType] = Array.from(
        { length: 12 },
        () => ({ sum: 0, count: 0 })
      );
    }

    // sum up the time and increase the counter
    averageProcessingTimePerFindingType[currentFindingType][currentMonth]
      .count++;
    averageProcessingTimePerFindingType[currentFindingType][
      currentMonth
    ].sum += currentProcessingTime;
  });

  // calculate average for every month of every finding type
  Object.keys(averageProcessingTimePerFindingType).forEach(currFindingType => {
    averageProcessingTimePerFindingType[currFindingType].forEach(
      currentMonthData => {
        currentMonthData.average =
          currentMonthData.count === 0
            ? 0
            : currentMonthData.sum / currentMonthData.count;
      }
    );
  });

  // chart js conversion
  const datasets = Object.keys(averageProcessingTimePerFindingType).map(
    currFindingType => {
      return {
        label: currFindingType,
        data: averageProcessingTimePerFindingType[currFindingType].map(v =>
          v.average.toFixed(2)
        )
      };
    }
  );

  return {
    labels: monthsLabels,
    datasets
  };
}

/**
 * Calculates a dataset combining the amount of measures
 * created per month and the average processing time per
 * month per finding type
 *
 * @param data The raw data to use for conversion
 */
export function calculateCombinedMeasuresPerMonthAverageProcessingTimeData(
  data
) {
  const measuresPerMonthData = calculateMeasuresPerMonthData(data);
  const averageProcessingTimeData = calculateAverageProcessingTimeData(data);
  return {
    datasets: [
      {
        label: "Measures created per month",
        data: measuresPerMonthData.datasets[0].data,
        type: "line",
        fill: false
      },
      ...averageProcessingTimeData.datasets
    ],
    labels: monthsLabels
  };
}

/**
 * Calculates the proportions of the different finding types
 *
 * @param data The raw data to use for conversion
 */
export function calculateFingingsTypesProportionData(data) {
  const amountOfFindingTypes = data.reduce((acc, curr) => {
    const currentFinding = curr.findingTypeText;
    acc[currentFinding] = (acc[currentFinding] || 0) + 1;
    return acc;
  }, {});

  // chart js conversion
  const chartLabels: string[] = [];
  const chartData: number[] = [];

  Object.keys(amountOfFindingTypes).forEach(key => {
    chartLabels.push(key);
    chartData.push(amountOfFindingTypes[key]);
  });

  return {
    datasets: [{ data: chartData }],
    labels: chartLabels
  };
}

/**
 * Calculates the proportions of the different measure types
 *
 * @param data The raw data to use for conversion
 */
export function calculateMeasureTypesProportionData(data) {
  const dataWithMeasureType = data.filter(v => "measureMeasuretype" in v);

  const amountOfMeasureTypes = dataWithMeasureType.reduce((a, c) => {
    const currentMeasure = c["measureMeasuretype"];
    a[currentMeasure] = (a[currentMeasure] || 0) + 1;
    return a;
  }, {});

  // chart js conversion
  const chartLabels: string[] = [];
  const chartData: number[] = [];

  Object.keys(amountOfMeasureTypes).forEach(key => {
    chartLabels.push(key);
    chartData.push(amountOfMeasureTypes[key]);
  });

  return {
    datasets: [{ data: chartData }],
    chartLabels
  };
}

/**
 * Calculates the amount of measures updated after due date
 *
 * @param data The raw data to use for conversion
 */
export function calculateMeasureDueDateProportionData(data) {
  const dataWithMeasure = data.filter(v => "measureId" in v);

  const measuresAfterDueDate = dataWithMeasure.filter(v => {
    const dueDate = moment(v["measureDueDate"]);
    const lastUpdated = moment(v["measureLastUpdateTime"]);
    return lastUpdated.isAfter(dueDate);
  });

  // chart js conversion
  const labels: string[] = [
    "Last updated after due date",
    "Last updated before due date"
  ];
  const chartData: number[] = [
    measuresAfterDueDate.length,
    dataWithMeasure.length - measuresAfterDueDate.length
  ];

  return {
    datasets: [{ data: chartData }],
    labels
  };
}
